import {
  DataGrid,
  type GridColDef,
  GridColumnHeaderParams,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid'
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Error as ErrorIcon, DataUsage } from '@mui/icons-material'
import { Alert, IconButton, Tooltip, Typography, Box } from '@mui/material'
import DeviceInfoButton from '@/components/DeviceInfoButton'
import { layoutStyles } from '@/utils/theme'
import { useFetchDevicePage } from '@/api/devicePage'
import { DefaultFetchDeviceFilters, DurationTestStatus } from '@/api/definition'
import { useFilters } from '@/hooks/useFilters'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { FilterDrawer } from '@/components/FilterDrawer'
import type { Device } from '@/pages/Device/Device'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import types from '@/pages/Device/DeviceType'

type Props = {
  enableFilters?: boolean
  enableSearch?: boolean
}

const DurationTestResultsStep = ({
  enableFilters = true,
  enableSearch = true,
}: Props): React.JSX.Element => {
  const { t } = useTranslation(['app'])
  const { fetchDevicePage } = useFetchDevicePage()
  const { urlState, updateUrlState } = useTableUrlState()

  // Local state only for drawer
  const localFilterHooks = useFilters()
  const filterConfig = useDeviceTypeFilters()

  // Data state
  const [data, setData] = useState<Device[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const abortController = useRef(new AbortController())

  // Local search state - initialize once, never sync back
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel
  const selectedFilters = urlState.filters

  // Data fetching function
  const fetchData = useCallback(async () => {
    abortController.current.abort()
    abortController.current = new AbortController()

    setIsLoading(true)
    setError(null)

    try {
      const sortBy =
        sortModel.length > 0
          ? [{ id: sortModel[0].field, desc: sortModel[0].sort === 'desc' }]
          : []

      const result = await fetchDevicePage(
        {
          ...DefaultFetchDeviceFilters,
          inService: true,
          selfTest: 0,
          durationTestStatus: [DurationTestStatus.DurationTestVerify],
        },
        {
          pageIndex: page,
          pageSize: pageSize,
          sortBy,
          globalFilter: searchTerm,
          selectedFilters: enableFilters ? selectedFilters : {},
        },
        true, // pickTestProgress
      )

      const transformedData = result.data?.length
        ? result.data.map((device) => ({ ...device, id: device.uid }))
        : []

      setData(transformedData)
      setRowCount(result.rowCount ?? 0)
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError') {
        setError(error.message ?? 'Failed to load data')
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    page,
    pageSize,
    sortModel,
    searchTerm,
    selectedFilters,
    enableFilters,
    fetchDevicePage,
  ])

  // Fetch when state changes
  useEffect(() => {
    fetchData()
    return () => {
      abortController.current.abort()
    }
  }, [fetchData])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      updateUrlState({
        pageIndex: newModel.page,
        pageSize: newModel.pageSize,
      })
    },
    [updateUrlState],
  )

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: newSortModel,
      })
    },
    [updateUrlState],
  )

  //debounced search (no sync back)
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately for responsive UI
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 200)
    },
    [updateUrlState],
  )

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...selectedFilters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0,
        filters: newFilters,
      })
    },
    [selectedFilters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = selectedFilters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [selectedFilters, handleFilterChange],
  )

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 400,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 120,
        sortable: true,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 120,
        sortable: true,
      },
      {
        field: 'isOnline',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        flex: 0.8,
        minWidth: 100,
        sortable: true,
        renderCell: (params) =>
          params.value ? t('common:general.yes') : t('common:general.no'),
      },
      {
        field: 'type',
        headerName: t('di:device.type'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          const deviceTypes = types as Record<string, string>
          const deviceType = deviceTypes[params.value] ?? params.value
          const deviceRoleName = params.row.deviceRoleName

          if (deviceRoleName) {
            return (
              <Tooltip title={deviceRoleName} placement="top" arrow>
                <Box
                  component="span"
                  sx={{ display: 'inline-block', width: '100%' }}
                >
                  {deviceType}
                </Box>
              </Tooltip>
            )
          }

          return deviceType
        },
        flex: 1,
        minWidth: 120,
        sortable: true,
      },
      {
        field: 'isPass',
        headerName: t('di:device.status'),
        renderHeader: renderWrappedHeader,
        flex: 0.6,
        minWidth: 90,
        sortable: true,
        renderCell: (params) => {
          const MINIMUM_GPS_MESSAGE_COUNT = 6
          const MINIMUM_HEARTBEAT_MESSAGE_COUNT = 480
          const MAXIMUM_DISCONNECTS_ALLOWED = 2

          const gpsPass =
            (params.row.noOfGPSMessages ?? 0) >= MINIMUM_GPS_MESSAGE_COUNT
          const hbPass =
            (params.row.noOfHeartbeats ?? 0) >= MINIMUM_HEARTBEAT_MESSAGE_COUNT
          const discPass =
            (params.row.noOfDisconnects ?? 0) <= MAXIMUM_DISCONNECTS_ALLOWED

          const errorCodes = (params.row.concerns ?? []).filter(
            (c: { error_code?: number; code?: number }) =>
              c?.error_code ?? c?.code,
          )

          const passed =
            gpsPass && hbPass && discPass && errorCodes.length === 0

          return passed ? (
            <Check sx={{ color: 'success.main' }} />
          ) : (
            <ErrorIcon sx={{ color: 'warning.main' }} />
          )
        },
      },
      {
        field: 'deviceInfo',
        headerName: '',
        renderHeader: renderWrappedHeader,
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => <DeviceInfoButton params={params} />,
      },
      {
        field: 'testResult',
        headerName: '',
        renderHeader: renderWrappedHeader,
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title={t('device.durationTest.viewTestResult')}>
            <IconButton
              size="small"
              onClick={() =>
                window.open(
                  `/v2/main/duration-test/${params.row.uid}`,
                  '_blank',
                )
              }
              aria-label={t('device.durationTest.viewTestResult')}
            >
              <DataUsage color="warning" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [t],
  )

  if (error) {
    return (
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  const shouldShowCustomToolbar = enableFilters || enableSearch

  const activeFilterCount = enableFilters
    ? Object.values(selectedFilters).reduce(
        (count, group) => count + Object.keys(group).length,
        0,
      )
    : 0

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Box
          sx={{
            borderRadius: '4px',
            overflow: 'auto',
            width: '100%',
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
            disableVirtualization
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            loading={isLoading}
            sx={{
              borderRadius: '4px',
            }}
            slots={
              shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
            }
            slotProps={{
              toolbar: shouldShowCustomToolbar
                ? ({
                    onFilterClick: enableFilters
                      ? localFilterHooks.openDrawer
                      : undefined,
                    activeFilterCount: activeFilterCount,
                    enableFilters: enableFilters,
                    enableSearch: enableSearch,
                    selectedFilters: selectedFilters,
                    filterConfig: filterConfig,
                    onRemoveFilter: handleRemoveFilter,
                    searchValue: localSearchTerm,
                    onSearchChange: handleSearchChange,
                  } as CustomToolbarProps)
                : undefined,
              pagination: {
                showFirstButton: true,
                showLastButton: true,
                ActionsComponent: TablePaginationActions,
              },
            }}
            disableColumnFilter={true}
            disableRowSelectionOnClick={true}
            disableColumnMenu={true}
            disableDensitySelector={true}
            disableColumnSelector={true}
            localeText={{ noRowsLabel: 'No records found' }}
          />
        </Box>
        {enableFilters && filterConfig && (
          <FilterDrawer
            open={localFilterHooks.isDrawerOpen}
            onClose={localFilterHooks.closeDrawer}
            filterConfig={filterConfig}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
          />
        )}
      </Box>
    </>
  )
}

export default DurationTestResultsStep
